import { t } from '@lingui/core/macro';
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useCurrentUser } from '../../services/userHooks';
import { useUpdateTags } from '../../services/bookmarkActions';
import { useBookmark } from '../../services/dataHooks';
import { filterTags, tagExists } from '../../services/tagService';
import { scrollToSelectedElement } from '../../utils/scrollUtils';
import { TagInput, CurrentTagsContainer, AvailableTagsList } from '../Tag/TagComponents';
import type { Bookmark } from '../../types/api';

interface ReaderTagPopoverProps {
  bookmarkId: string;
  closePopover?: () => void;
  initialBookmark?: Bookmark; // Optional prop to avoid initial state jump
}

function ReaderTagPopover({
  bookmarkId,
  closePopover,
  initialBookmark,
}: ReaderTagPopoverProps) {
  const { currentUser: user } = useCurrentUser();
  const { bookmark: fetchedBookmark } = useBookmark(bookmarkId);
  // Always prefer fetchedBookmark when available (it's reactive to cache updates)
  // Only use initialBookmark as a fallback when fetchedBookmark hasn't loaded yet
  const bookmark = useMemo(() => {
    if (!fetchedBookmark && !initialBookmark) return undefined;
    if (!fetchedBookmark) return initialBookmark;
    // Always use fetchedBookmark when available since it's reactive to cache updates
    return fetchedBookmark;
  }, [fetchedBookmark, initialBookmark]);
  const updateTags = useUpdateTags();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Memoize derived values to prevent unnecessary re-renders
  const currentTags = useMemo(() => bookmark?.tags || [], [bookmark?.tags]);
  const availableTags = useMemo(() => user?.tags || [], [user?.tags]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Filter available tags based on search and exclude current tags
  const filteredTags = useMemo(
    () => filterTags(searchTerm, availableTags, currentTags),
    [searchTerm, availableTags, currentTags],
  );

  // Check if we should show the "Create tag" option
  const shouldShowCreateTag = useMemo(() => {
    const trimmedSearch = searchTerm.trim();
    return (
      trimmedSearch.length > 0 &&
      !tagExists(trimmedSearch, availableTags) &&
      !tagExists(trimmedSearch, currentTags)
    );
  }, [searchTerm, availableTags, currentTags]);

  // Calculate total items for keyboard navigation
  const totalItems = filteredTags.length + (shouldShowCreateTag ? 1 : 0);

  // Reset selected index when filtered results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchTerm]);

  const handleAddTag = (tagName: string) => {
    updateTags.mutate(
      { bookmarkId, addTags: [tagName] },
      {
        onSuccess: () => {
          setSearchTerm('');
          inputRef.current?.focus();
        },
        onError: (error) => {
          console.error('Failed to add tag:', error);
        },
      },
    );
  };

  const handleRemoveTag = (tagName: string) => {
    updateTags.mutate(
      { bookmarkId, removeTags: [tagName] },
      {
        onSuccess: () => {
          inputRef.current?.focus();
        },
      },
    );
  };

  const handleCreateAndAddTag = () => {
    const tagName = searchTerm.trim();
    if (tagName && !tagExists(tagName, currentTags)) {
      handleAddTag(tagName);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'Enter':
        e.preventDefault();
        if (shouldShowCreateTag && selectedIndex === 0) {
          handleCreateAndAddTag();
        } else {
          const adjustedIndex = shouldShowCreateTag ? selectedIndex - 1 : selectedIndex;
          if (adjustedIndex >= 0 && adjustedIndex < filteredTags.length) {
            handleAddTag(filteredTags[adjustedIndex].name);
          }
        }
        break;
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % totalItems);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + totalItems) % totalItems);
        break;
      case 'Escape':
        e.preventDefault();
        closePopover?.();
        break;
    }
  };

  // Scroll selected item into view
  useEffect(() => {
    scrollToSelectedElement(listRef);
  }, [selectedIndex]);

  return (
    <div className="tag-actions-container" onClick={(e) => e.stopPropagation()}>
      <TagInput
        value={searchTerm}
        onChange={setSearchTerm}
        onKeyDown={handleKeyDown}
        placeholder={t`Add tag`}
        autoFocus={true}
      />

      <CurrentTagsContainer
        tags={currentTags}
        onRemoveTag={handleRemoveTag}
        isPending={updateTags.isPending}
      />

      <AvailableTagsList
        tags={filteredTags}
        onTagClick={(tag) => handleAddTag(tag.name)}
        onCreateTag={handleCreateAndAddTag}
        createTagName={searchTerm.trim()}
        showCreateTag={shouldShowCreateTag}
        selectedIndex={selectedIndex}
        disabled={updateTags.isPending}
        showCount={true}
        emptyMessage={searchTerm ? t`No matching tags found` : t`No available tags`}
      />
    </div>
  );
}

export default ReaderTagPopover;
